import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import AppUpload from '../components/AppUpload';
import TimeAgo from '../components/TimeAgo';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import './Apps.css';

const Apps = () => {
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const { dialogState, closeDialog, confirm, alert } = useDialog();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState('all');

    useEffect(() => {
        if (currentWorkspace) {
            fetchApps();
        }
    }, [currentWorkspace, selectedPlatform]);

    const fetchApps = async () => {
        try {
            setLoading(true);
            const params = selectedPlatform !== 'all' ? { platform: selectedPlatform } : {};
            const response = await api.get('/apps', { params });
            setApps(response.data.rows || []);
            setError(null);
        } catch (err) {
            setError('Failed to load apps');
            console.error('Error fetching apps:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadSuccess = (newApp) => {
        setApps([newApp, ...apps]);
        setShowUpload(false);
    };

    const handleDelete = async (appId) => {
        const confirmed = await confirm({
            title: 'Delete App',
            message: 'Are you sure you want to delete this app? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await api.delete(`/apps/${appId}`);
            setApps(apps.filter(app => app.id !== appId));
        } catch (err) {
            await alert({
                title: 'Error',
                message: 'Failed to delete app. Please try again.',
                variant: 'danger'
            });
            console.error('Error deleting app:', err);
        }
    };

    const handleDownload = async (appId, appName) => {
        try {
            const response = await api.get(`/apps/${appId}/download`);
            window.open(response.data.downloadUrl, '_blank');
        } catch (err) {
            await alert({
                title: 'Error',
                message: 'Failed to generate download link. Please try again.',
                variant: 'danger'
            });
            console.error('Error downloading app:', err);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return formatDate(dateString);
    };

    const getPlatformIcon = (platform) => {
        return platform === 'android' ? '🤖' : '🍎';
    };

    const getHealthIcon = (status) => {
        const icons = {
            healthy: '🟢',
            flaky: '🟡',
            failing: '🔴'
        };
        return icons[status] || '⚪';
    };

    const getUploadMethodBadge = (method) => {
        const badges = {
            file: { label: 'File', color: '#4CAF50' },
            url: { label: 'URL', color: '#2196F3' },
            api: { label: 'CI/CD', color: '#FF9800' }
        };
        const badge = badges[method] || badges.file;
        return (
            <span className="upload-method-badge" style={{ backgroundColor: badge.color }}>
                {badge.label}
            </span>
        );
    };

    if (!currentWorkspace) {
        return (
            <div className="apps-container">
                <div className="no-workspace">
                    <p>Please select a workspace to manage apps</p>
                </div>
            </div>
        );
    }

    return (
        <div className="apps-container">
            <div className="apps-header">
                <div className="header-left">
                    <h1>App Management</h1>
                    <p>Upload and manage your mobile applications</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                    + Upload App
                </button>
            </div>

            {showUpload && (
                <AppUpload
                    onSuccess={handleUploadSuccess}
                    onClose={() => setShowUpload(false)}
                />
            )}

            <div className="apps-filters">
                <div className="filter-group">
                    <label>Platform:</label>
                    <select
                        value={selectedPlatform}
                        onChange={(e) => setSelectedPlatform(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Platforms</option>
                        <option value="android">Android</option>
                        <option value="ios">iOS</option>
                    </select>
                </div>
                <div className="apps-count">
                    {apps.length} app{apps.length !== 1 ? 's' : ''}
                </div>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading apps...</p>
                </div>
            ) : apps.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>No apps uploaded yet</h3>
                    <p>Upload your first mobile app to get started with testing</p>
                    <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                        Upload Your First App
                    </button>
                </div>
            ) : (
                <div className="apps-grid">
                    {apps.map((app) => (
                        <Link
                            key={app.id}
                            to={`/apps/${app.id}`}
                            className="app-card clickable"
                        >
                            {/* Health Indicator Badge */}
                            {app.healthStatus && (
                                <div className={`health-badge ${app.healthStatus}`}>
                                    {getHealthIcon(app.healthStatus)}
                                </div>
                            )}

                            <div className="app-card-header">
                                <div className="app-platform">
                                    <span className="platform-icon">{getPlatformIcon(app.platform)}</span>
                                    <span className="platform-name">{app.platform}</span>
                                </div>
                                {getUploadMethodBadge(app.uploadMethod)}
                            </div>

                            <div className="app-card-body">
                                <div className="app-info-row">
                                    {/* App Icon */}
                                    <div className="app-icon-container">
                                        {app.metadata?.appInfo?.icon ? (
                                            <img
                                                src={app.metadata.appInfo.icon}
                                                alt={app.displayName || app.name}
                                                className="apps_listing__app-icon"
                                            />
                                        ) : (
                                            <div className="app-icon-fallback">
                                                {getPlatformIcon(app.platform)}
                                            </div>
                                        )}
                                    </div>

                                    {/* App Details */}
                                    <div className="app-details">
                                        <h3 className="app-name">{app.displayName || app.name}</h3>

                                        {/* Latest Build Info */}
                                        {/* {app.version && (
                                            <div className="build-info">
                                                <span className="build-version">v{app.version}</span>
                                                {app.buildNumber && (
                                                    <span className="build-number">Build {app.buildNumber}</span>
                                                )}
                                            </div>
                                        )} */}

                                        {/* Last Test Result */}
                                        {app.lastTestResult && (
                                            <div className={`test-result-badge ${app.lastTestResult}`}>
                                                {app.lastTestResult === 'pass' ? '✅ Last test passed' : '❌ Last test failed'}
                                                {app.lastTestedAt && (
                                                    <span className="test-time"> · <TimeAgo date={app.lastTestedAt} /></span>
                                                )}
                                            </div>
                                        )}

                                        {app.packageName && (
                                            <div className="app-package">{app.packageName}</div>
                                        )}
                                        <br />
                                        <div className="upload-date">
                                            Uploaded <TimeAgo date={app.createdAt} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            <Dialog {...dialogState} onClose={closeDialog} />
        </div>
    );
};

export default Apps;
